// Dessin de la carte de partage sur un canvas 2D : cadre aux couleurs du thème, nom et numéro, fenêtre du portrait, puis deux colonnes
// séparées par un filet : à gauche le caractère du kami (un petit paragraphe tiré de sa divinité), à droite « Play ZERO KAMI » avec son QR
// code. Toute la mise en page est ici, en coordonnées de la carte (CARD_WIDTH × CARD_HEIGHT) : le canvas s'affiche ensuite à n'importe
// quelle taille, et l'image enregistrée garde la pleine définition. Le QR code est toujours noir sur blanc (fiabilité du scan).
export const CARD_WIDTH = 750;
export const CARD_HEIGHT = 1050;

const FONT_TITLE = "'Fredoka', 'Inter', ui-sans-serif, system-ui, sans-serif";
const FONT_TEXT = "'Inter', ui-sans-serif, system-ui, sans-serif";

/** Couleurs du thème courant (variables CSS de styles/themes.css), avec des repli si elles manquent. */
export function themeColors(root = document.documentElement) {
  const style = getComputedStyle(root);
  const read = (name, fallback) => style.getPropertyValue(name).trim() || fallback;
  return {
    background: read('--app-bg', '#0d0712'),
    accent: read('--pumpkin', '#ff7518'),
    accentBright: read('--accent-bright', '#ff9d1f'),
    secondary: read('--witch', '#a855f7'),
  };
}

function roundedRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Coupe un texte en lignes de largeur au plus `maxWidth` ; la dernière est tronquée par « … » au-delà de `maxLines`. */
export function wrapText(ctx, text, maxWidth, maxLines = 3) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
  kept[maxLines - 1] = `${last}…`;
  return kept;
}

/** Proportions du logo, les mêmes que celles du logo de l'écran d'accueil et de l'en-tête (voir .logo dans index.html). */
export const LOGO = Object.freeze({
  gap: 0.24, // l'espace entre « ZERO » et « KAMI », en fraction de la taille
});

/**
 * Le logo « ZERO KAMI » centré sur `cx` : « ZERO » en blanc, puis « KAMI » avec son dégradé, en capitales, même police et même taille, sur la même ligne
 * de base (plus de zéro chiffré en exposant : « 0kami » se lisait « Okami », la marque de Capcom, #10). Si `maxWidth` est donné, la taille
 * est réduite pour que le logo y tienne (la colonne du QR code de la carte est étroite). Renvoie la taille utilisée.
 */
export function drawLogo(ctx, cx, baseline, size, colors, maxWidth = Infinity) {
  const measure = (px, text) => { ctx.font = `700 ${px}px ${FONT_TITLE}`; return ctx.measureText(text).width; };
  const widthAt = (px) => measure(px, 'ZERO') + px * LOGO.gap + measure(px, 'KAMI');
  const natural = widthAt(size);
  const px = natural > maxWidth ? Math.floor(size * (maxWidth / natural)) : size;
  const font = `700 ${px}px ${FONT_TITLE}`;
  const zeroWidth = measure(px, 'ZERO');
  const kamiWidth = measure(px, 'KAMI');
  const left = cx - (zeroWidth + px * LOGO.gap + kamiWidth) / 2;
  const kamiX = left + zeroWidth + px * LOGO.gap;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = font;
  ctx.fillStyle = '#ffffff';
  ctx.fillText('ZERO', left, baseline);
  const gradient = ctx.createLinearGradient(kamiX, 0, kamiX + kamiWidth, 0);
  gradient.addColorStop(0, colors.accentBright);
  gradient.addColorStop(1, colors.secondary);
  ctx.fillStyle = gradient;
  ctx.fillText('KAMI', kamiX, baseline);
  return px;
}

/** Colonnes du bas de carte : le caractère à gauche, le QR code et son texte à droite, séparés par un filet. */
export const LAYOUT = Object.freeze({
  art: { x: 52, y: 134, w: CARD_WIDTH - 104, h: 380 },
  band: { top: 546, bottom: 990 },
  left: { x: 60, w: 380 },
  divider: { x: 468 },
  right: { x: 498, w: 192 }, // largeur de la colonne du QR code ; centrée sur x + w / 2
});

/** Le caractère : jusqu'à 9 lignes de 40 px sous le titre de colonne ; la consigne du QR code, jusqu'à 4 lignes. */
export const STORY_MAX_LINES = 9;
export const STORY_LINE_HEIGHT = 40;
export const SCAN_MAX_LINES = 4;

/** Écrit des lignes centrées ou alignées à gauche, l'une sous l'autre ; renvoie l'ordonnée de la ligne suivante. */
function writeLines(ctx, lines, x, y, lineHeight) {
  lines.forEach((line, i) => ctx.fillText(line, x, y + i * lineHeight));
  return y + lines.length * lineHeight;
}

/**
 * Dessine la carte.
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} card      voir core/shareCard.js#cardForKami
 * @param {object} options
 * @param {CanvasImageSource|null} options.portrait  portrait du kami (déjà chargé) ; sans lui, la fenêtre reste vide
 * @param {{ size: number, isDark: (row: number, col: number) => boolean }} options.qr  matrice du QR code
 * @param {object} options.colors  couleurs du thème (voir themeColors)
 * @param {object} options.text  textes dans la langue courante : number, character, playLead, scan, footer
 */
export function paintShareCard(ctx, card, { portrait = null, qr, colors, text }) {
  const W = CARD_WIDTH;
  const H = CARD_HEIGHT;
  const { art, band, left, divider, right } = LAYOUT;
  ctx.clearRect(0, 0, W, H);

  // Cadre : dégradé du thème, puis fond sombre à l'intérieur
  roundedRect(ctx, 0, 0, W, H, 44);
  const frame = ctx.createLinearGradient(0, 0, W, H);
  frame.addColorStop(0, colors.accentBright);
  frame.addColorStop(0.55, colors.accent);
  frame.addColorStop(1, colors.secondary);
  ctx.fillStyle = frame;
  ctx.fill();
  roundedRect(ctx, 14, 14, W - 28, H - 28, 32);
  const body = ctx.createLinearGradient(0, 0, 0, H);
  body.addColorStop(0, '#201236');
  body.addColorStop(1, colors.background);
  ctx.fillStyle = body;
  ctx.fill();

  // Nom et numéro
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = `700 76px ${FONT_TITLE}`;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(card.name, 52, 112);
  ctx.textAlign = 'right';
  ctx.font = `800 28px ${FONT_TEXT}`;
  ctx.fillStyle = colors.accentBright;
  ctx.fillText(text.number, W - 52, 108);

  // Fenêtre du portrait, teintée d'après le kami
  roundedRect(ctx, art.x, art.y, art.w, art.h, 26);
  const artFill = ctx.createRadialGradient(W / 2, art.y + art.h * 0.45, 20, W / 2, art.y + art.h * 0.45, art.w * 0.7);
  artFill.addColorStop(0, `hsl(${card.hue}, 70%, 38%)`);
  artFill.addColorStop(1, `hsl(${(card.hue + 40) % 360}, 65%, 12%)`);
  ctx.fillStyle = artFill;
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(255,255,255,0.28)';
  ctx.stroke();
  if (portrait) {
    const sw = portrait.naturalWidth || portrait.width || 1;
    const sh = portrait.naturalHeight || portrait.height || 1;
    const scale = Math.min((art.w - 40) / sw, (art.h - 30) / sh);
    const dw = sw * scale;
    const dh = sh * scale;
    ctx.drawImage(portrait, art.x + (art.w - dw) / 2, art.y + (art.h - dh) / 2, dw, dh);
  }

  // Filet entre les deux colonnes
  ctx.beginPath();
  ctx.moveTo(divider.x, band.top + 8);
  ctx.lineTo(divider.x, band.bottom - 8);
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.stroke();

  // Colonne de gauche : le caractère du kami, un petit texte
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `800 24px ${FONT_TEXT}`;
  ctx.fillStyle = colors.accentBright;
  ctx.fillText(text.character.toUpperCase(), left.x, band.top + 34);
  ctx.font = `italic 600 30px ${FONT_TEXT}`;
  ctx.fillStyle = '#e2e8f0';
  writeLines(ctx, wrapText(ctx, card.story, left.w, STORY_MAX_LINES), left.x, band.top + 82, STORY_LINE_HEIGHT);

  // Colonne de droite : QR code blanc (marge de lecture incluse), « Play ZERO KAMI » (le logo) et sa consigne, centrés
  const centerX = right.x + right.w / 2;
  const qrBox = right.w;
  const qrX = right.x;
  const qrY = band.top + 6;
  roundedRect(ctx, qrX, qrY, qrBox, qrBox, 14);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  const quiet = 2; // modules de marge blanche
  const cell = (qrBox - 2 * 6) / (qr.size + 2 * quiet);
  const origin = qrX + 6 + quiet * cell;
  ctx.fillStyle = '#000000';
  for (let row = 0; row < qr.size; row += 1) {
    for (let col = 0; col < qr.size; col += 1) {
      if (qr.isDark(row, col)) ctx.fillRect(origin + col * cell, qrY + 6 + quiet * cell + row * cell, Math.ceil(cell * 100) / 100, Math.ceil(cell * 100) / 100);
    }
  }
  // « Joue à » puis le logo ZERO KAMI
  ctx.textAlign = 'center';
  ctx.font = `700 30px ${FONT_TITLE}`;
  const playGradient = ctx.createLinearGradient(right.x, 0, right.x + right.w, 0);
  playGradient.addColorStop(0, colors.accentBright);
  playGradient.addColorStop(1, colors.secondary);
  ctx.fillStyle = playGradient;
  const leadBaseline = qrY + qrBox + 44;
  ctx.fillText(text.playLead, centerX, leadBaseline);
  const logoBaseline = leadBaseline + 52;
  drawLogo(ctx, centerX, logoBaseline, 44, colors, right.w);
  const afterPlay = logoBaseline + 36;
  ctx.textAlign = 'center';
  ctx.font = `600 22px ${FONT_TEXT}`;
  ctx.fillStyle = '#cbd5e1';
  writeLines(ctx, wrapText(ctx, text.scan, right.w, SCAN_MAX_LINES), centerX, afterPlay + 8, 28);

  // Pied : version et marque (™, jamais ®)
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `400 20px ${FONT_TEXT}`;
  ctx.fillStyle = '#94a3b8';
  ctx.fillText(text.footer, W / 2, H - 31);
}
